import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, ChevronRight, FileText, Loader2, TrendingUp } from 'lucide-react';
import type { Profile, Projeto, Tarefa } from '../../types';
import type { Progresso } from '../../src/lib/projetos/progresso';
import type { MembroEquipe } from '../../src/lib/projetos/equipe';
import {
  calcularPrazoProjeto, tarefasEmAtencao, distribuicaoPorStatus, proximasEntregas, calcularRitmo,
} from '../../src/lib/projetos/visaoGeral';
import { carregarAtividadeProjeto } from '../../src/lib/projetos/carregarAtividade';
import type { ItemAtividade } from '../../src/lib/projetos/timeline';
import { ComentarioTexto, corDoUsuario } from '../../src/components/comentarios/ComentarioTexto';
import { rotuloPrazo } from '../../src/lib/projetos/tarefasProjeto';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import TaskDescriptionMarkdown from '../Tarefas/TaskDescriptionMarkdown';
import type { FiltroTarefas } from './ProjetoTarefasPanel';

const STATUS_BARRA: Record<string, { rotulo: string; cor: string }> = {
  novo: { rotulo: 'Novo', cor: 'bg-gray-300 dark:bg-gray-500' },
  em_analise: { rotulo: 'Em análise', cor: 'bg-indigo-400' },
  em_desenvolvimento: { rotulo: 'Em desenvolvimento', cor: 'bg-sky-500' },
  em_teste: { rotulo: 'Em teste', cor: 'bg-violet-500' },
  concluido: { rotulo: 'Concluído', cor: 'bg-emerald-500' },
};

/** Quantos itens o bloco "Precisa de atenção" mostra antes de mandar para a aba Tarefas. */
const MAX_ATENCAO = 6;
const MAX_ATIVIDADE = 3;

const dataBR = (v: string) => new Date(`${v.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR');

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

const CARD = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl';

interface Props {
  projeto: Projeto;
  tarefas: Tarefa[];
  users: Profile[];
  equipe: MembroEquipe[];
  progresso: Progresso;
  documentos: number;
  faltaDocumentacao: boolean;
  onAbrirTarefas: (filtro: FiltroTarefas) => void;
  onAbrirAba: (aba: 'documentacao' | 'atividade') => void;
}

const ProjetoVisaoGeral: React.FC<Props> = ({
  projeto, tarefas, users, equipe, progresso, documentos, faltaDocumentacao, onAbrirTarefas, onAbrirAba,
}) => {
  const hoje = useMemo(() => new Date(), []);
  const prazo = useMemo(
    () => calcularPrazoProjeto(projeto.prazo, projeto.etapa, tarefas, hoje),
    [projeto.prazo, projeto.etapa, tarefas, hoje],
  );
  const atencao = useMemo(() => tarefasEmAtencao(tarefas, hoje), [tarefas, hoje]);
  const distribuicao = useMemo(() => distribuicaoPorStatus(tarefas), [tarefas]);
  const proximas = useMemo(() => proximasEntregas(tarefas, hoje), [tarefas, hoje]);
  const ritmo = useMemo(() => calcularRitmo(tarefas, projeto.created_at, hoje), [tarefas, projeto.created_at, hoje]);
  const maxSemana = Math.max(1, ...ritmo.semanas.map(s => s.concluidas));
  const diasAlemDoPrazo = ritmo.previsao && projeto.prazo
    ? Math.round((new Date(`${ritmo.previsao}T00:00:00`).getTime() - new Date(`${projeto.prazo.slice(0, 10)}T00:00:00`).getTime()) / 86_400_000)
    : null;

  // Atividade recente: mesma fonte da aba Atividade, só as últimas.
  const [atividade, setAtividade] = useState<ItemAtividade[] | null>(null);
  const chaveTarefas = tarefas.map(t => t.id).join(',');
  const mencionaveis = useMemo(() => tarefas.map(t => ({ id: t.id, titulo: t.titulo })), [tarefas]);
  useEffect(() => {
    let vivo = true;
    carregarAtividadeProjeto(projeto.id, tarefas.map(t => t.id), new Map(tarefas.map(t => [String(t.id), t.titulo])))
      .then(itens => { if (vivo) setAtividade(itens.slice(0, MAX_ATIVIDADE)); })
      .catch(() => { if (vivo) setAtividade([]); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto.id, chaveTarefas]);

  const nomeDoUsuario = (uid?: string | null) => users.find(u => u.id === uid)?.full_name || 'Sem responsável';
  const tituloDaMae = (pid?: number | string | null) => tarefas.find(t => String(t.id) === String(pid))?.titulo;

  // ─── card de prazo ──────────────────────────────────────────────────────────
  const prazoCard = (() => {
    const d = prazo.dias ?? 0;
    switch (prazo.situacao) {
      case 'atrasado': return { valor: `${plural(-d, 'dia', 'dias')} de atraso`, tom: 'rose' as const };
      case 'vence_hoje': return { valor: 'Vence hoje', tom: 'amber' as const };
      case 'proximo': return { valor: `Faltam ${plural(d, 'dia', 'dias')}`, tom: 'amber' as const };
      case 'no_prazo': return { valor: `Faltam ${plural(d, 'dia', 'dias')}`, tom: 'neutro' as const };
      case 'encerrado': return { valor: projeto.etapa === 'concluido' ? 'Concluído' : 'Cancelado', tom: 'neutro' as const };
      default: return { valor: 'Sem prazo', tom: 'neutro' as const };
    }
  })();
  const TOM_CARD = {
    rose: 'border-rose-200 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-900/20',
    amber: 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-900/20',
    neutro: '',
  };
  const TOM_TEXTO = {
    rose: 'text-rose-600 dark:text-rose-400',
    amber: 'text-amber-700 dark:text-amber-300',
    neutro: 'text-gray-800 dark:text-white',
  };

  const kpis: { rotulo: string; valor: number; filtro: FiltroTarefas; alerta: boolean }[] = [
    { rotulo: 'Tarefas', valor: progresso.total, filtro: 'todas', alerta: false },
    { rotulo: 'Concluídas', valor: progresso.concluidas, filtro: 'concluidas', alerta: false },
    { rotulo: 'Em aberto', valor: progresso.total - progresso.concluidas, filtro: 'abertas', alerta: false },
    { rotulo: 'Atrasadas', valor: progresso.atrasadas, filtro: 'atrasadas', alerta: progresso.atrasadas > 0 },
  ];

  const abertasSemInicio = distribuicao.segmentos.find(s => s.status === 'novo')?.quantidade || 0;
  const emAberto = progresso.total - progresso.concluidas;

  return (
    <div className="space-y-4">
      {/* KPIs: o prazo do projeto primeiro, porque é a pergunta que a tela respondia mal —
          a data ficava só no cabeçalho, sem dizer que já tinha passado. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className={`${CARD} ${TOM_CARD[prazoCard.tom]} p-4 col-span-2 md:col-span-1`}>
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <CalendarClock size={12} /> Prazo{projeto.prazo ? ` · ${dataBR(projeto.prazo)}` : ''}
          </p>
          <p className={`text-lg font-bold leading-tight mt-1 ${TOM_TEXTO[prazoCard.tom]}`}>{prazoCard.valor}</p>
        </div>
        {kpis.map(kpi => (
          <button key={kpi.rotulo} type="button" onClick={() => onAbrirTarefas(kpi.filtro)}
            className={`${CARD} p-4 text-left hover:border-primary/50 hover:shadow-sm transition group`}
            title={`Ver ${kpi.rotulo.toLowerCase()} na aba Tarefas`}>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
              {kpi.rotulo}
              <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className={`text-2xl font-bold ${kpi.alerta ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-white'}`}>
              {kpi.valor}
            </p>
          </button>
        ))}
      </div>

      {prazo.cronogramaEstoura && prazo.ultimoVencimentoAberto && projeto.prazo && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            O cronograma das tarefas termina em <strong>{dataBR(prazo.ultimoVencimentoAberto)}</strong>, depois do
            prazo do projeto ({dataBR(projeto.prazo)}). Ajuste o prazo ou os vencimentos para os dois baterem.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Precisa de atenção */}
        <div className={`${CARD} p-5`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-800 dark:text-white">
              Precisa de atenção <span className="font-normal text-gray-400">({atencao.length})</span>
            </h2>
            {progresso.atrasadas > 0 && (
              <button type="button" onClick={() => onAbrirTarefas('atrasadas')} className="text-xs text-primary hover:underline">
                Ver atrasadas
              </button>
            )}
          </div>
          {atencao.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <CheckCircle2 size={16} className="text-emerald-500" />
              Nada atrasado nem vencendo nos próximos 3 dias.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {atencao.slice(0, MAX_ATENCAO).map(({ tarefa, situacao }) => {
                const atrasada = situacao === 'atrasada';
                const mae = tarefa.parent_id != null ? tituloDaMae(tarefa.parent_id) : null;
                return (
                  <li key={tarefa.id}>
                    <button type="button" onClick={() => abrirSolicitacao(tarefa.id)}
                      className={`w-full text-left flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 transition-colors ${
                        atrasada
                          ? 'border-l-rose-500 bg-rose-50/70 hover:bg-rose-50 dark:bg-rose-900/15'
                          : 'border-l-amber-400 bg-amber-50/60 hover:bg-amber-50 dark:bg-amber-900/15'
                      }`}>
                      {atrasada
                        ? <AlertTriangle size={14} className="shrink-0 mt-0.5 text-rose-500" />
                        : <Clock size={14} className="shrink-0 mt-0.5 text-amber-500" />}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-gray-800 dark:text-white truncate">{tarefa.titulo}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                          {nomeDoUsuario(tarefa.responsavel_id)}
                          {mae && ` · subtarefa de ${mae}`}
                        </span>
                      </span>
                      <span className={`shrink-0 text-[11px] font-bold whitespace-nowrap ${
                        atrasada ? 'text-rose-600 dark:text-rose-400' : 'text-amber-700 dark:text-amber-300'
                      }`}>
                        {rotuloPrazo(tarefa, hoje)}
                      </span>
                    </button>
                  </li>
                );
              })}
              {atencao.length > MAX_ATENCAO && (
                <li>
                  <button type="button" onClick={() => onAbrirTarefas('abertas')} className="text-xs text-primary hover:underline px-3">
                    + {atencao.length - MAX_ATENCAO} na aba Tarefas
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Onde o trabalho está */}
        <div className={`${CARD} p-5`}>
          <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-1">Onde o trabalho está</h2>
          {distribuicao.total === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma tarefa vinculada ainda.</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {emAberto === 0
                  ? 'Todas as tarefas concluídas.'
                  : `${plural(emAberto, 'tarefa em aberto', 'tarefas em aberto')}, ${abertasSemInicio === emAberto ? 'nenhuma iniciada' : abertasSemInicio === 0 ? 'todas já iniciadas' : `${abertasSemInicio} ainda sem início`}.`}
              </p>
              <div className="flex w-full h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                {distribuicao.segmentos.map(s => (
                  <div key={s.status}
                    className={STATUS_BARRA[s.status]?.cor || 'bg-gray-400'}
                    style={{ width: `${(s.quantidade / distribuicao.total) * 100}%` }}
                    title={`${STATUS_BARRA[s.status]?.rotulo || s.status}: ${s.quantidade}`} />
                ))}
              </div>
              <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {distribuicao.segmentos.map(s => (
                  <li key={s.status} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                    <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${STATUS_BARRA[s.status]?.cor || 'bg-gray-400'}`} />
                    <span className="flex-1 truncate">{STATUS_BARRA[s.status]?.rotulo || s.status}</span>
                    <span className="font-semibold tabular-nums">{s.quantidade}</span>
                  </li>
                ))}
              </ul>
              {distribuicao.descartadas > 0 && (
                <p className="mt-3 text-[11px] text-gray-400">
                  {plural(distribuicao.descartadas, 'rejeitada/cancelada fora da conta', 'rejeitadas/canceladas fora da conta')}.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Próximas entregas */}
        <div className={`${CARD} p-5`}>
          <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-3">Próximas entregas</h2>
          {proximas.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma tarefa em aberto com vencimento pela frente.</p>
          ) : (
            <ol className="relative border-l-2 border-gray-100 dark:border-gray-700 ml-1.5 space-y-3">
              {proximas.map(t => {
                const r = rotuloPrazo(t, hoje);
                return (
                  <li key={t.id} className="pl-4 relative">
                    <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                      r ? 'bg-amber-400' : 'bg-gray-300 dark:bg-gray-500'
                    }`} />
                    <button type="button" onClick={() => abrirSolicitacao(t.id)} className="text-left w-full group">
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-primary truncate">{t.titulo}</span>
                        <span className={`shrink-0 text-xs font-semibold tabular-nums ${r ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}`}>
                          {dataBR(t.vencimento)}
                        </span>
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {nomeDoUsuario(t.responsavel_id)}{r && ` · ${r.toLowerCase()}`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Ritmo e projeção */}
        <div className={`${CARD} p-5`}>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
              <TrendingUp size={14} className="text-gray-400" /> Ritmo
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400"
              title="Média de tarefas concluídas por semana nos últimos 14 dias (ou desde o início, se o projeto é mais novo)">
              <strong className="text-base text-gray-800 dark:text-white tabular-nums">
                {ritmo.porSemana.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
              </strong> concluídas/semana
            </span>
          </div>

          {/* Concluídas por semana — a semana atual em destaque */}
          <div className="flex items-end gap-2 h-20" role="img"
            aria-label={`Concluídas por semana: ${ritmo.semanas.map(s => `${dataBR(s.inicio)} ${s.concluidas}`).join(', ')}`}>
            {ritmo.semanas.map((s, i) => (
              <div key={s.inicio} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 tabular-nums">{s.concluidas}</span>
                <div className={`w-full max-w-[40px] rounded-t ${i === ritmo.semanas.length - 1 ? 'bg-primary' : 'bg-primary/40'}`}
                  style={{ height: `${Math.max(4, (s.concluidas / maxSemana) * 100)}%` }} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-1">
            {ritmo.semanas.map((s, i) => (
              <span key={s.inicio} className="flex-1 text-center text-[10px] text-gray-400">
                {i === ritmo.semanas.length - 1 ? 'esta sem.' : dataBR(s.inicio).slice(0, 5)}
              </span>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-sm">
            {ritmo.motivo === 'ok' && ritmo.previsao && (
              <>
                <p className="text-gray-700 dark:text-gray-200">
                  No ritmo atual, {ritmo.restantes === 1 ? 'a tarefa restante termina' : `as ${ritmo.restantes} tarefas restantes terminam`} por
                  volta de <strong>{dataBR(ritmo.previsao)}</strong>.
                </p>
                {diasAlemDoPrazo != null && (
                  diasAlemDoPrazo > 0 ? (
                    <p className="text-xs font-semibold text-rose-600 dark:text-rose-400 mt-1">
                      {plural(diasAlemDoPrazo, 'dia', 'dias')} depois do prazo do projeto ({dataBR(projeto.prazo as string)}).
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1">Dentro do prazo do projeto.</p>
                  )
                )}
                <p className="text-[11px] text-gray-400 mt-1">Estimativa pela média das últimas 2 semanas — muda conforme o time entrega.</p>
              </>
            )}
            {ritmo.motivo === 'poucos_dados' && (
              <p className="text-gray-500 dark:text-gray-400">Ainda há poucas entregas para projetar uma data de término com segurança.</p>
            )}
            {ritmo.motivo === 'sem_ritmo' && (
              <p className="text-rose-600 dark:text-rose-400">
                Nenhuma tarefa concluída nas últimas 2 semanas — sem ritmo para projetar o término.
              </p>
            )}
            {ritmo.motivo === 'concluido' && (
              <p className="text-emerald-600 dark:text-emerald-400">Todas as tarefas concluídas.</p>
            )}
          </div>
        </div>
      </div>

      {/* Quem está no projeto sai das tarefas vinculadas — o painel mostra a divisão real do
          trabalho e com quem o atraso está, que é o que o cabeçalho só resume em avatares. */}
      <div className={`${CARD} p-5`}>
        <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-3">
          Equipe <span className="font-normal text-gray-400">({equipe.length})</span>
        </h2>
        {equipe.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ninguém atribuído ainda. Defina o responsável do projeto ou vincule solicitações com dono.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {equipe.map(m => {
              const u = users.find(x => x.id === m.userId);
              const nome = u?.full_name || 'Usuário removido';
              const abertas = m.tarefas - m.concluidas;
              const pct = m.tarefas > 0 ? Math.round((m.concluidas / m.tarefas) * 100) : 0;
              return (
                <li key={m.userId} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  {u?.avatar_url ? (
                    <img src={u.avatar_url} alt={nome} className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-500 dark:text-gray-300 shrink-0">
                      {nome.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{nome}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {m.papel === 'gestor' ? 'Responsável pelo projeto' : 'Participante'}
                    </p>
                  </div>
                  {m.tarefas === 0 ? (
                    <span className="shrink-0 text-xs text-gray-400">sem tarefa</span>
                  ) : (
                    <div className="shrink-0 flex items-center gap-3">
                      {m.atrasadas > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                          <AlertTriangle size={10} /> {plural(m.atrasadas, 'atrasada', 'atrasadas')}
                        </span>
                      )}
                      <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {plural(abertas, 'aberta', 'abertas')}
                      </span>
                      <div className="w-28">
                        <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">
                          <span>{m.concluidas}/{m.tarefas}</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Atividade recente */}
        <div className={`${CARD} p-5 lg:col-span-2`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-800 dark:text-white">Atividade recente</h2>
            <button type="button" onClick={() => onAbrirAba('atividade')} className="text-xs text-primary hover:underline">
              Ver tudo
            </button>
          </div>
          {atividade === null ? (
            <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin" /> Carregando...</p>
          ) : atividade.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma movimentação ainda.</p>
          ) : (
            <ul className="space-y-2.5">
              {atividade.map(item => {
                const paleta = corDoUsuario(item.userId || '');
                const autor = users.find(u => u.id === item.userId)?.full_name || 'Alguém';
                return (
                  <li key={item.id} className={`border-l-4 ${paleta.barra} pl-3`}>
                    <div className="flex flex-wrap items-center gap-x-2 text-xs">
                      <span className={`font-bold ${paleta.nome}`}>{autor}</span>
                      <span className="text-gray-400">
                        {new Date(item.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="text-gray-400 truncate">
                        · {item.origem.tipo === 'projeto' ? 'Projeto' : item.origem.titulo}
                      </span>
                    </div>
                    {item.tipo === 'status' ? (
                      <p className="text-sm italic text-gray-500 dark:text-gray-400">{item.conteudo}</p>
                    ) : (
                      <ComentarioTexto texto={item.conteudo} usuarios={users} tarefas={mencionaveis}
                        className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Documentação */}
        <div className={`${CARD} p-5 ${faltaDocumentacao ? 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-900/20' : ''}`}>
          <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-2 flex items-center gap-1.5">
            <FileText size={14} className="text-gray-400" /> Documentação
          </h2>
          {faltaDocumentacao ? (
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Projeto concluído sem documentação. Registre o que foi entregue antes que o contexto se perca.
            </p>
          ) : documentos === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nada documentado ainda.</p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">{plural(documentos, 'item documentado', 'itens documentados')}.</p>
          )}
          <button type="button" onClick={() => onAbrirAba('documentacao')}
            className="mt-3 text-xs font-semibold text-primary hover:underline">
            {documentos === 0 ? 'Documentar' : 'Abrir documentação'}
          </button>
        </div>
      </div>

      {projeto.descricao && (
        <div className={`${CARD} p-5`}>
          <TaskDescriptionMarkdown value={projeto.descricao} variant="full" />
        </div>
      )}
    </div>
  );
};

export default ProjetoVisaoGeral;
